import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Skeleton, Text } from '@feathr/components';
import { useStore } from '@feathr/extender/state';

interface IInitiatedByLineProps {
  /** The run's start date, already formatted. */
  date: string;
  /** The run's start time, already formatted. */
  time: string;
  /** The importer's `created_by`. Absent when no initiator was recorded. */
  userId?: string;
}

/**
 * "Initiated by <name> on <date> at <time>".
 *
 * Resolves `userId` through the `Users` collection, which caches by id, so a page of imports
 * started by the same person fetches them once. With no user id, or a user who can't be read
 * (removed from the account, or Feathr staff outside it), the line drops the name rather than
 * leaving a gap in the sentence.
 */
function InitiatedByLine({ date, time, userId }: Readonly<IInitiatedByLineProps>): JSX.Element {
  const { t } = useTranslation();
  const { Users } = useStore();

  const user = userId ? Users.get(userId) : undefined;
  if (user?.isPending) {
    return <Skeleton />;
  }

  // `User.name` prefers first + last over the stale raw `name`, but falls back to "Unnamed User";
  // like UserSelect, use the email instead when the user has no name at all.
  const hasName = !!(user?.firstName || user?.lastName || user?.get('name', '').trim());
  const readableUser = user && !user.isErrored ? user : undefined;
  const name = hasName ? readableUser?.name : readableUser?.get('email', '').trim();

  // Values interpolate unescaped (`{{- }}`): React escapes the text, and i18next's own escaping
  // would print a date's slashes, or an apostrophe in a name, as HTML entities.

  return (
    <Text.DefaultRegular>
      {name
        ? t('Initiated by {{- name}} on {{- date}} at {{- time}}', { date, name, time })
        : t('Initiated on {{- date}} at {{- time}}', { date, time })}
    </Text.DefaultRegular>
  );
}

export default observer(InitiatedByLine);
